import { useEffect } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSidebarLinkClicked } from '@/analytics/utils/sidebar-analytics'
import { Link } from 'react-router-dom'
import { NewChat, PaxtonLogoMark, SidePeekLeft } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'
import { useNewChatMatterBoundAction } from '@/chat-common/new_chat/hooks/useNewChatMatterBoundAction'
import { SideNavRailItem } from '../side-nav-item/SideNavRailItem'
import { SideNavRailMatterRecord } from './SideNavRailMatterRecord'
import { PAXTON_LOGO_MARK_URL, SIDENAV_CONTRACTED_WIDTH } from './constants'
import type { SideNavMatterRecordsConfig, SideNavItemConfig } from './SideNav'

export type SideNavRailProps = {
  /** Navigation items, in the order they appear in the rail */
  items: SideNavItemConfig[]
  /** Optional Matter Records section, rendered as a flyout rather than an inline section */
  matterRecords?: SideNavMatterRecordsConfig
  /**
   * Shows the New Chat action. Set when the expanded nav is showing its Chat History section — the
   * chat list itself cannot fit in the rail, so only its New Chat action carries over.
   */
  showNewChat?: boolean
  /** The id of the currently open chat, so New Chat reads as selected while it's the viewer's unsent chat */
  activeChatId?: string
  /** ID of the currently active item for highlight state */
  activeItemId?: string
  /** Expands the nav back to full width */
  onExpand: () => void
  /** Ref to the expand toggle, so the owner can move focus here when the nav contracts. */
  expandToggleRef?: React.Ref<HTMLButtonElement>
  /** Called after any nav item click completes */
  onNavigate?: () => void
  /** Render prop for footer content (account menu) */
  renderFooter: () => React.ReactNode
  /** The compact logo mark. The rail has no room for the full-width logo the expanded nav uses. */
  logoUrl: string
}

/**
 * The contracted side nav: an icon rail carrying the same destinations as the expanded nav.
 *
 * Every item is icon-only, so each carries an `aria-label` and a tooltip on the right.
 *
 * For the expanded nav, use SideNavDashboard / SideNavMatter.
 */
export function SideNavRail({
  items,
  matterRecords,
  showNewChat = false,
  activeChatId,
  activeItemId,
  onExpand,
  expandToggleRef,
  onNavigate,
  renderFooter,
  logoUrl,
}: SideNavRailProps) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { handleButtonClick, unsentChatId, clickDisabled, createError } = useNewChatMatterBoundAction(onNavigate)

  // The expanded nav renders this failure inline; the rail has nowhere to put a message that
  // wouldn't push the footer off the bottom, so it reports through a toast instead.
  useEffect(() => {
    if (!createError) return

    dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Error creating chat. Please try again.', durationMs: kToastDurationMediumMs }))
  }, [createError, dispatch])

  return (
    <div data-ds-component="SideNavRail" className="flex h-full flex-col border-r bg-surface-content" style={{ width: SIDENAV_CONTRACTED_WIDTH }}>
      {/* Stacking the expand toggle above the logo mark is why this header is taller than the
          expanded nav's. */}
      <div className="flex h-[88px] shrink-0 flex-col items-center gap-4 border-b py-3">
        <IconButton
          ref={expandToggleRef}
          aria-label="Expand navigation"
          tooltipText="Expand"
          disableTooltipInteractive
          tooltipPlacement="right"
          aria-expanded={false}
          icon={<SidePeekLeft className="h-5 w-5" />}
          onClick={onExpand}
          size="sm"
          variant="plain"
          color="neutral"
          className="rounded-md"
        />
        <Link to="/dashboard" aria-label="Home">
          {/* The link carries the accessible name, so the mark itself is decorative. */}
          {logoUrl === PAXTON_LOGO_MARK_URL ? (
            <PaxtonLogoMark aria-hidden className="h-6 w-6 text-brand-500" />
          ) : (
            <img className="h-6 w-6 object-contain" src={logoUrl} alt="" />
          )}
        </Link>
      </div>

      <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto py-2">
        {items.map((item) => (
          <SideNavRailItem
            key={item.id}
            to={item.href}
            icon={item.railIcon ?? item.icon}
            label={item.label}
            variant={item.variant}
            isActive={activeItemId === item.id}
            onClick={() => {
              trackSidebarLinkClicked(trackEvent, item.id, true)
              item.onClick?.()
              onNavigate?.()
            }}
          />
        ))}

        {matterRecords && <SideNavRailMatterRecord matterId={matterRecords.matterId} activeItemId={activeItemId} onNavigate={onNavigate} />}

        {showNewChat && (
          <SideNavRailItem
            icon={NewChat}
            label="New Chat"
            tone="brand"
            isActive={!!activeChatId && activeChatId === unsentChatId}
            disabled={clickDisabled}
            onClick={handleButtonClick}
          />
        )}
      </nav>

      <div className="flex h-12 shrink-0 items-center justify-center border-t">{renderFooter()}</div>
    </div>
  )
}
